/* eslint-disable complexity */
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Sidebar } from '@futrli/ui-components';
import Typography from 'carbon-react/lib/components/typography';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { consolidationByIdSelector, organisationByIdSelector, ROUTES, StoreState } from '@futrli/shared-services';
import { CheckForToken } from '@futrli-sdk/predict';
import { UILoader, UILoaderSpinner } from '@futrli/ui-components';

import { checkForToken, exportForecast, fetchOauthURL } from '../../../../../core/api/external-sheets';
import { PeriodType, PERIOD_TYPE_QUERY_PARAM, TRACKING_EVENTS } from '../../../../../core/constants';
import { trackEvent } from '../../../../../core/tracker';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { getRedirectURL } from '../url-utils';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { getExportFilename } from '../utils';

interface Props {
  onClose: () => void;
  forecastId?: string;
  forecastKind?: 'budget' | 'scenario' | 'live';
  forecastName?: string;
  isConsolidation: boolean;
}

export const ExportDrawer: FC<Props> = ({
  onClose,
  forecastId: propsForecastId,
  forecastName,
  forecastKind,
  isConsolidation
}: Props) => {
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { periodType } = usePredictionsInfoContext();
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();

  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;

  const intl = useIntl();

  const forecastIdToUse = forecastId || propsForecastId || '';

  const queryParams = periodType ? `?${PERIOD_TYPE_QUERY_PARAM}=${periodType}` : '';

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authURL, setAuthURL] = useState<string>('');
  const [sheetURL, setSheetURL] = useState<string>('');
  const [error, setError] = useState<string>('');

  const consolidationName = useSelector((store: StoreState) => {
    return consolidationByIdSelector(store.normalised, organisationId)?.name;
  });

  const organisationName = useSelector((store: StoreState) => {
    return organisationByIdSelector(store.normalised, workspaceId, organisationId)?.name;
  });

  const entityName = consolidationName || organisationName || '';

  useEffect(() => {
    const handleCheckForTokenAndExport = async () => {
      let checkForTokenResult: CheckForToken | undefined;
      try {
        checkForTokenResult = await checkForToken();
      } catch {
        setError('Failed to check for token');
        addErrorMessage?.(new Error('Failed to check export authorisation status'));
        setIsLoading(false);
        return;
      }

      // if no token generate auth url or perform an export
      // in either case a button will be shown to open the result in a new tab
      if (!checkForTokenResult.token_already_exists) {
        let error = '';

        try {
          const exportURL = getRedirectURL(
            workspaceId,
            organisationId,
            forecastIdToUse as string,
            ROUTES.EXPORT,
            queryParams
          );
          const oauthResult = await fetchOauthURL(exportURL);
          if (oauthResult.error) {
            error = 'Failed to authorise export';
          } else {
            setAuthURL(oauthResult.url as string);
            setIsLoading(false);
          }
        } catch {
          error = 'Failed to authorise export';
        }

        if (error) {
          setError(error);
          addErrorMessage?.(new Error(error));
          setIsLoading(false);
        }
      } else {
        try {
          const exportResult = await exportForecast(
            workspaceId,
            organisationId,
            forecastIdToUse as string,
            getExportFilename(entityName, intl, forecastName, forecastKind, periodType || 'MONTHLY'),
            periodType,
            isConsolidation
          );
          exportResult?.sheet_url && setSheetURL(exportResult.sheet_url);
          trackEvent(TRACKING_EVENTS.EXPORTED_REPORT);
          setIsLoading(false);
        } catch {
          setError('Failed to export forecast');
          addErrorMessage?.(new Error('Failed to export forecast'));
          setIsLoading(false);
        }
      }
    };
    handleCheckForTokenAndExport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewExport = () => {
    window.open(sheetURL, '_blank');
    onClose();
  };

  const handleAuthorisationAction = async () => {
    window.open(authURL, '_blank');
    onClose();
  };

  return (
    <Sidebar
      header={
        <Typography variant="h2" fontWeight="700">
          <FormattedMessage id="predict.export_report" defaultMessage="Export report" />
        </Typography>
      }
      onCancel={onClose}
      open={true}
    >
      {isLoading ? (
        <UILoader />
      ) : (
        <Form
          stickyFooter
          leftSideButtons={
            <Button onClick={onClose} disabled={isLoading} data-testid="export-dialog-actions-secondary">
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          }
          saveButton={
            sheetURL ? (
              <Button
                buttonType="primary"
                disabled={isLoading}
                onClick={handleViewExport}
                data-testid="export-dialog-actions-view-export"
              >
                <FormattedMessage id="common.view_export" defaultMessage="View export" />
              </Button>
            ) : authURL ? (
              <Button
                buttonType="primary"
                disabled={isLoading}
                onClick={handleAuthorisationAction}
                data-testid="export-dialog-actions-primary"
              >
                <FormattedMessage id="common.continue" defaultMessage="Continue" />
              </Button>
            ) : null
          }
          onSubmit={sheetURL ? handleViewExport : authURL ? handleAuthorisationAction : undefined}
        >
          {isLoading && <UILoaderSpinner />}
          {authURL && (
            <p data-testid="export-dialog-auth-required">
              <FormattedMessage
                id="predict.export_report_google_sheets_log_in_access"
                defaultMessage="We will export your report to Google Sheets and will need you to log in to your Google account.{br}{br}This will only give us access to the files and folders we have created."
                values={{
                  br: <br />
                }}
              />
            </p>
          )}
          {sheetURL && <ExportSuccessMessage periodType={periodType} />}
          {error && (
            <p data-testid="export-dialog-auth-error">
              <FormattedMessage
                id="predict.there_is_issue_with_export_try_again"
                defaultMessage="There has been an issue with your export. Please try again and contact support if it persists."
              />
            </p>
          )}
        </Form>
      )}
    </Sidebar>
  );
};

const ExportSuccessMessage: React.FC<{ periodType?: PeriodType }> = ({ periodType }) => {
  const intl = useIntl();
  return (
    <React.Fragment>
      <p data-testid="export-dialog-export-complete">
        <FormattedMessage
          id="predict.report_successfully_exported"
          defaultMessage="Your report has been successfully exported to Google Sheets."
        />
      </p>

      {periodType && (
        <React.Fragment>
          <br />
          <p data-testid="export-dialog-export-complete-period">
            <FormattedMessage
              id="predict.all_numbers_displayed_value_in_line"
              defaultMessage="All numbers will be displayed {thisPeriod} in line with how they are being displayed in Predict. Please change the view if you want these numbers to be exported {otherPeriod}."
              values={{
                thisPeriod:
                  periodType === 'MONTHLY'
                    ? intl.formatMessage({ id: 'common.monthly', defaultMessage: 'Monthly' }).toLocaleLowerCase()
                    : intl.formatMessage({ id: 'common.weekly', defaultMessage: 'Weekly' }).toLocaleLowerCase(),
                otherPeriod:
                  periodType === 'MONTHLY'
                    ? intl.formatMessage({ id: 'common.weekly', defaultMessage: 'Weekly' }).toLocaleLowerCase()
                    : intl.formatMessage({ id: 'common.monthly', defaultMessage: 'Monthly' }).toLocaleLowerCase()
              }}
            />
          </p>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
